import { Routes, Route, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useWedding } from '../../context/WeddingContext'
import {
  Heart, LayoutDashboard, Calendar, Users,
  Gift, Camera, BookOpen, LogOut, Crown, Sparkles, Settings, ExternalLink, X, DoorOpen
} from 'lucide-react'
import { useState, useEffect } from 'react'
import DashboardHome from './DashboardHome'
import EventsPage from './events/EventsPage'
import EventDetail from './events/EventDetail'
import BudgetPage from './budget/BudgetPage'
import PlanningPage from './planning/PlanningPage'
import RegistryPage from './registry/RegistryPage'
import RegistryGiftsPage from './registrygifts/RegistryGiftsPage'
import CountdownPage from './CountdownPage'
import ProfilePage from './profile/ProfilePage'
import GiftLogPage from './gifts/GiftLogPage'
import WeddingDayPage from './weddingday/WeddingDayPage'
import GuestsPage from './guests/GuestsPage'
import SettingsPage from './settings/SettingsPage'
import AsoEbiPage from './asoebi/AsoEbiPage'
import AislePage from '../aisle/AislePage'
import { useFeatureFlags, FlagGate } from '../../lib/featureFlags'
import ChatWidget from '../../components/ChatWidget'
import CelebrationToast from '../../components/CelebrationToast'
import WisdomFloat from '../../components/WisdomFloat'
import OnboardingTour from '../../components/OnboardingTour'
import { supabase } from '../../lib/supabase'

// Aso Ebi icon — from public folder
function AsoEbiIcon({ size=18, color='currentColor' }) {
  return <img src="/aso_ebi_icon.png" alt="Aso Ebi" style={{ width:size, height:size, objectFit:'contain', opacity: color==='currentColor'?1:1 }}/>
}

const NAV = [
  { to:'/dashboard',               label:'Overview',         icon:LayoutDashboard, exact:true },
  { to:'/dashboard/events',        label:'My Events',        icon:Calendar,   tour:'nav-events'   },
  { to:'/dashboard/guests',        label:'Guests & RSVP',    icon:Users,      tour:'nav-guests'   },
  { to:'/dashboard/profile',       label:'Our Profile',      icon:Camera,     tour:'nav-profile',  tabs:3 },
  { to:'/dashboard/planning',      label:'Planning',         icon:Calendar,   tour:'nav-planning', tabs:2 },
  { to:'/dashboard/registrygifts', label:'Registry & Gifts', icon:Gift,       tour:'nav-registry', tabs:2 },
  { to:'/dashboard/asoebi',        label:'Aso Ebi',          icon:AsoEbiIcon, tour:'nav-asoebi', flag:'asoebi' },
  { to:'/dashboard/aisle',         label:'Okoiya Aisle',     icon:DoorOpen,   tour:'nav-aisle',  flag:'aisle'  },
  { to:'/dashboard/programme',     label:'Countdown',        icon:BookOpen },
  { to:'/dashboard/weddingday',    label:'Wedding Day',      icon:Sparkles },
  { to:'/dashboard/settings',      label:'Settings',         icon:Settings },
]

// ── Source icons ──────────────────────────────────────────
const IgIcon  = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/></svg>
const TkIcon  = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.15 8.15 0 004.77 1.52V6.77a4.85 4.85 0 01-1-.08z"/></svg>
const YtIcon  = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M23 7s-.3-2-1.2-2.8c-1.1-1.2-2.4-1.2-3-1.3C16.2 3 12 3 12 3s-4.2 0-6.8.2c-.6 0-1.9.1-3 1.3C1.3 5 1 7 1 7S.7 9.2.7 11.4v2c0 2.2.3 4.4.3 4.4s.3 2 1.2 2.8c1.1 1.2 2.6 1.1 3.2 1.2C7.2 22 12 22 12 22s4.2 0 6.8-.3c.6 0 1.9-.1 3-1.3.9-.8 1.2-2.8 1.2-2.8s.3-2.2.3-4.4v-2C23.3 9.2 23 7 23 7zM9.7 15.5V8.4l8.1 3.6-8.1 3.5z"/></svg>
const GgIcon  = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>

const DISCOVERY_SOURCES = [
  { key:'instagram', label:'Instagram',        Icon:IgIcon },
  { key:'tiktok',    label:'TikTok',           Icon:TkIcon },
  { key:'youtube',   label:'YouTube',          Icon:YtIcon },
  { key:'friend',    label:'Friend told me',   Icon:()=><Users size={17}/> },
  { key:'google',    label:'Google',           Icon:GgIcon },
  { key:'vendor',    label:'Wedding vendor',   Icon:()=><Crown size={17}/> },
  { key:'church',    label:'Church/community', Icon:()=><Heart size={17}/> },
  { key:'other',     label:'Other',            Icon:()=><span style={{fontSize:'1rem',lineHeight:1}}>•••</span> },
]

function DiscoveryCard({ weddingId, answered, visible, onRecorded }) {
  const [show, setShow]   = useState(false)
  const [picked, setPicked] = useState(false)
  // Per-WEDDING dismiss flag (not per-browser) so every couple is asked once,
  // and the source of truth for "answered" is the DB column, not localStorage.
  const dismissKey = weddingId ? `okoiya_source_asked_${weddingId}` : 'okoiya_source_asked'

  useEffect(() => {
    if (!visible || !weddingId) return
    if (answered) return                                  // already told us (DB)
    if (localStorage.getItem(dismissKey) === '1') return  // dismissed for this wedding
    const t = setTimeout(() => setShow(true), 500)
    return () => clearTimeout(t)
  }, [visible, weddingId, answered])

  async function pick(key) {
    setShow(false)
    if (!weddingId) { setPicked(true); return }
    const { error } = await supabase.from('weddings').update({ discovery_source: key }).eq('id', weddingId)
    if (error) {
      // Don't pretend it saved — log it and leave it un-dismissed so the couple
      // is asked again next session rather than the answer being lost silently.
      console.warn('[discovery] could not save source:', error.message)
      return
    }
    setPicked(true)
    onRecorded?.()   // refresh wedding so answered=true and we know it persisted
  }

  function dismiss() {
    setShow(false)
    localStorage.setItem(dismissKey, '1')
  }

  if (!show || picked) return null

  return (
    <>
      <style>{`
        @keyframes discSlideUp {
          from { opacity:0; transform:translateY(20px); }
          to   { opacity:1; transform:translateY(0); }
        }
      `}</style>
      <div style={{
        position:'fixed', bottom:88, left:'50%', transform:'translateX(-50%)',
        zIndex:45, width:'min(520px,calc(100vw - 32px))',
        background:'white', border:'1px solid #E5E0D8',
        borderRadius:'1.25rem', boxShadow:'0 8px 40px rgba(0,0,0,0.12)',
        padding:'1rem 1.125rem 1rem',
        animation:'discSlideUp 0.35s cubic-bezier(0.34,1.56,0.64,1) both',
      }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.75rem' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem', color:'#1A1A1A', margin:0 }}>
            Quick one — how did you find Okoiya?
          </p>
          <button onClick={dismiss} style={{ background:'none', border:'none', cursor:'pointer', color:'#BCBCBC', lineHeight:0, padding:'2px' }}>
            <X size={14}/>
          </button>
        </div>

        {/* Source chips */}
        <div style={{ display:'flex', flexWrap:'wrap', gap:'0.4rem' }}>
          {DISCOVERY_SOURCES.map(({ key, label, Icon }) => (
            <button key={key} onClick={() => pick(key)} style={{
              display:'flex', alignItems:'center', gap:'0.375rem',
              fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', fontWeight:600,
              color:'#4A3728', background:'#F8F4F0',
              border:'1px solid #E5E0D8', borderRadius:100,
              padding:'0.375rem 0.75rem', cursor:'pointer',
              transition:'all 0.15s',
            }}
              onMouseEnter={e=>{ e.currentTarget.style.background='rgba(196,130,106,0.1)'; e.currentTarget.style.borderColor='rgba(196,130,106,0.4)'; e.currentTarget.style.color='#C4826A' }}
              onMouseLeave={e=>{ e.currentTarget.style.background='#F8F4F0'; e.currentTarget.style.borderColor='#E5E0D8'; e.currentTarget.style.color='#4A3728' }}
            >
              <Icon/> {label}
            </button>
          ))}
        </div>
      </div>
    </>
  )
}

export default function Dashboard() {
  const {user, profile, signOut } = useAuth()
  const { wedding, loadingWedding, events , isPremium, refreshWedding } = useWedding()
  const todayIsEventDay = events?.some(ev => ev.date && new Date(ev.date).toDateString()===new Date().toDateString())
  const navigate = useNavigate()
  const location = useLocation()
  // Land at the top of every page when navigating between dashboard routes
  // (the dashboard scrolls the window, so the position would otherwise persist).
  useEffect(() => { window.scrollTo({ top: 0, left: 0 }) }, [location.pathname])
  const [sidebarOpen, setSidebarOpen]     = useState(false)
  const { flags } = useFeatureFlags()
  const [tourComplete, setTourComplete]   = useState(() => localStorage.getItem('okoiya_tour_done') === '1')

  async function handleSignOut() { await signOut(); navigate('/') }
  const coupleNames = wedding ? `${wedding.partner1_name} & ${wedding.partner2_name}` : 'Your Wedding'
  // Controls only the sidebar Admin link's visibility — the /admin page itself is
  // server-gated (admin-stats returns 403 to non-admins), so this is UI-only.
  // Keep in sync with SUPER_ADMINS in the edge functions.
  const isAdmin = ['agamediaservices@gmail.com'].includes((user?.email || '').toLowerCase())

  // If user is logged in but has no wedding, redirect to onboarding
  useEffect(() => {
    if (user && !loadingWedding && !wedding) {
      navigate('/onboarding')
    }
  }, [user, wedding, loadingWedding, navigate])

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#FAFAF8' }}>
      <aside style={{ width:240, flexShrink:0, background:'white', borderRight:'1px solid #E5E0D8', display:'flex', flexDirection:'column', position:'fixed', top:0, left:0, height:'100vh', zIndex:40, transform:sidebarOpen?'translateX(0)':'translateX(-100%)', transition:'transform 0.3s ease' }} className="sidebar-desktop">

        <div style={{ padding:'1.5rem 1.25rem 1rem' }}>
          <img src="/okoiya-logo.png" alt="Okoiya" style={{ height:30, objectFit:'contain' }}/>
          {wedding && (
            <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'0.84rem', color:'#1A1A1A', margin:'0.6rem 0 0', lineHeight:1.3, letterSpacing:'0.007em' }}>
              {wedding.partner1_name} <span style={{ color:'#C4826A' }}>&</span> {wedding.partner2_name}
            </p>
          )}
        </div>
        {/* Ornamental divider */}
        <div style={{ padding:'0 1.25rem 0.875rem', display:'flex', alignItems:'center', gap:'0.5rem' }}>
          <div style={{ flex:1, height:'0.5px', background:'linear-gradient(to right, transparent, #E5D5CC)' }}/>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M6 0.5L7.2 4.5H11.5L8.1 7.1L9.3 11.1L6 8.5L2.7 11.1L3.9 7.1L0.5 4.5H4.8Z" fill="#DEC5BC"/>
          </svg>
          <div style={{ flex:1, height:'0.5px', background:'linear-gradient(to left, transparent, #E5D5CC)' }}/>
        </div>

        <nav style={{ flex:1, padding:'0.875rem 0.75rem', display:'flex', flexDirection:'column', gap:'1px', overflowY:'auto' }}>
          {NAV.filter(n => !n.flag || flags[n.flag] !== false).map(({ to, label, icon:Icon, exact, tour, tabs }) => (
            <NavLink key={to} to={to} end={exact} onClick={()=>setSidebarOpen(false)} {...(tour ? { 'data-tour': tour } : {})} style={({ isActive }) => ({
              display:'flex', alignItems:'center', gap:'0.625rem',
              padding:'0.55rem 0.75rem', borderRadius:'0.625rem',
              textDecoration:'none', fontFamily:'Nunito Sans, sans-serif',
              fontSize:'0.85rem', fontWeight:isActive?700:500,
              color:isActive?'#C4826A':'#6B6B6B',
              background:isActive?'rgba(196,130,106,0.08)':'transparent',
              transition:'all 0.15s'
            })}>
              <Icon size={16}/>
              <span style={{ flex:1 }}>{label}</span>
              {tabs > 1 && (
                <span title={`${tabs} sections inside`} style={{ display:'flex', gap:3, alignItems:'center', flexShrink:0 }}>
                  {Array.from({ length: tabs }).map((_, i) => (
                    <span key={i} style={{ width:4, height:4, borderRadius:'50%', background:'rgba(196,130,106,0.45)' }}/>
                  ))}
                </span>
              )}
              {label==='Wedding Day' && todayIsEventDay && (
                <span style={{ width:7,height:7,borderRadius:'50%',background:'#B36B55',display:'inline-block',animation:'livePulse 1.5s ease-in-out infinite',flexShrink:0 }}/>
              )}
            </NavLink>
          ))}
        </nav>
        {isAdmin && (
          <NavLink to="/admin" style={({ isActive }) => ({
            display:'flex', alignItems:'center', gap:'0.625rem',
            padding:'0.55rem 0.75rem', margin:'0 0.75rem',
            borderRadius:'0.625rem', textDecoration:'none',
            fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', fontWeight:500,
            color:'#C4826A', background:'rgba(196,130,106,0.06)',
            border:'1px solid rgba(196,130,106,0.2)'
          })}>
            ⚙ Admin Dashboard
          </NavLink>
        )}

        {!isPremium && (
          <div style={{ margin:'0 0.75rem 0.75rem', background:'linear-gradient(135deg,rgba(196,130,106,0.1),rgba(155,175,155,0.1))', border:'1px solid rgba(196,130,106,0.2)', borderRadius:'0.875rem', padding:'0.875rem' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', marginBottom:'0.35rem' }}>
              <Crown size={13} color="#C4826A"/>
              <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.73rem', fontWeight:700, color:'#C4826A' }}>Upgrade to Premium</span>
            </div>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'#6B6B6B', margin:'0 0 0.625rem', lineHeight:1.5 }}>Unlimited guests, all templates, QR check-in & more.</p>
            <button className="btn-primary" style={{ width:'100%', padding:'0.45rem', fontSize:'0.73rem', justifyContent:'center' }} onClick={()=>navigate('/pricing')}>See Plans</button>
          </div>
        )}

        <div style={{ borderTop:'1px solid #E5E0D8', padding:'0.875rem 1rem', display:'flex', alignItems:'center', gap:'0.625rem' }}>
          <div style={{ width:32, height:32, background:'white', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <span style={{ fontFamily:'Gibs, serif', fontSize:'0.875rem', color:'#C4826A' }}>{(profile?.full_name||'U')[0].toUpperCase()}</span>
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', fontWeight:700, color:'#1A1A1A', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{profile?.full_name||'Welcome'}</p>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:isPremium?'#9BAF9B':'#BCBCBC', margin:0, fontWeight:600 }}>{isPremium?'✦ Premium':'Free Plan'}</p>
          </div>
          <button onClick={handleSignOut} style={{ background:'none', border:'none', cursor:'pointer', color:'#BCBCBC', padding:'4px' }}><LogOut size={15}/></button>
        </div>
      </aside>

      <style>{`
        @media(min-width:768px){.sidebar-desktop{transform:translateX(0)!important}.main-content{margin-left:240px!important}}
      `}</style>

      <main className="main-content" style={{ flex:1, marginLeft:0, minHeight:'100vh', display:'flex', flexDirection:'column' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'1rem 1.25rem', borderBottom:'1px solid #E5E0D8', background:'white', position:'sticky', top:0, zIndex:30 }}>
          {/* Left: hamburger */}
          <button onClick={()=>setSidebarOpen(!sidebarOpen)} style={{ background:'none', border:'none', cursor:'pointer', display:'flex', flexDirection:'column', gap:'4px', padding:'4px', width:36 }}>
            {[0,1,2].map(i=><div key={i} style={{ width:20, height:2, background:'#1A1A1A', borderRadius:2 }}/>)}
          </button>
          {/* Center: logo */}
          <img src="/okoiya-logo.png" alt="Okoiya" style={{ height:28, position:'absolute', left:'50%', transform:'translateX(-50%)', objectFit:'contain' }}/>
          {/* Right: portal link */}
          <div style={{ display:'flex', alignItems:'center' }}>
          {wedding?.slug && (
            <button onClick={()=>window.open(`/w/${wedding.slug}`,'_blank')} title="Preview your guest portal"
              style={{ background:'none', border:'none', cursor:'pointer', color:'#6B6B6B', borderRadius:'50%', width:34, height:34, display:'flex', alignItems:'center', justifyContent:'center' }}
              onMouseEnter={e=>e.currentTarget.style.color='#C4826A'} onMouseLeave={e=>e.currentTarget.style.color='#6B6B6B'}>
              <ExternalLink size={16}/>
            </button>
          )}
          </div>{/* close right icons wrapper */}
        </div>

        <div style={{ flex:1, padding:'1rem', maxWidth:1200, width:'100%', margin:'0 auto', overflowX:'hidden', boxSizing:'border-box' }}>
          <Routes>
            <Route index element={<DashboardHome/>}/>
            <Route path="events" element={<EventsPage/>}/>
            <Route path="events/:eventId/*" element={<EventDetail/>}/>
            <Route path="guests/*" element={<GuestsPage/>}/>
            <Route path="planning/*" element={<PlanningPage/>}/>
            <Route path="asoebi" element={<FlagGate flag="asoebi"><AsoEbiPage/></FlagGate>}/>
            <Route path="aisle/*" element={<FlagGate flag="aisle"><AislePage/></FlagGate>}/>
            <Route path="budget/*" element={<BudgetPage/>}/>
            <Route path="registry/*" element={<RegistryPage/>}/>
            <Route path="registrygifts/*" element={<RegistryGiftsPage/>}/>
            <Route path="gifts/*" element={<GiftLogPage/>}/>
            <Route path="profile/*" element={<ProfilePage/>}/>
            <Route path="programme/*" element={<CountdownPage/>}/>
            <Route path="weddingday/*" element={<WeddingDayPage/>}/>
            <Route path="settings/*" element={<SettingsPage/>}/>
          </Routes>
        </div>
      </main>

      <CelebrationToast/>
      <WisdomFloat/>
      <ChatWidget/>
      <OnboardingTour onOpenSidebar={() => setSidebarOpen(true)} onTourComplete={() => setTourComplete(true)}/>
      <DiscoveryCard weddingId={wedding?.id} answered={!!wedding?.discovery_source} visible={tourComplete} onRecorded={refreshWedding}/>
      {sidebarOpen && <div onClick={()=>setSidebarOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.3)', zIndex:35, backdropFilter:'blur(2px)' }}/>}
    </div>
  )
}
